import { Trash2, Plus, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { FileUpload } from "@/components/FileUpload";
import { PersonInput } from "@/components/PersonInput";
import { Field, SimpleSelect } from "./Field";
import { LANGUAGES, ARTIST_ROLES, CONTRIBUTOR_ROLES } from "@/constants/catalog";
import { t } from "@/i18n";

const PeopleList = ({ items, roles, onChange, label, testId, defaultRole, kind }) => {
  const upd = (i, patch) => onChange(items.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{t(label)}</p>
        <button type="button" data-testid={`${testId}-add`} className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => onChange([...items, { name: "", role: defaultRole }])}><Plus className="h-3 w-3" /> {t("Agregar")}</button>
      </div>
      {items.map((p, i) => (
        <div key={i} className="grid grid-cols-[1fr_170px_32px] gap-2 items-center">
          <PersonInput value={p.name} onChange={(name) => upd(i, { name })} kind={kind} placeholder={t("Nombre")} testId={`${testId}-name-${i}`} />
          <SimpleSelect value={p.role} onChange={(v) => upd(i, { role: v })} options={roles} testId={`${testId}-role-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

export const TrackEditor = ({ track, index, onChange, onRemove }) => {
  const set = (patch) => onChange({ ...track, ...patch });
  const id = `track-${index}`;
  return (
    <div className="panel p-5 space-y-5 rise" data-testid={id}>
      <div className="flex items-center gap-3">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground w-6">{String(index + 1).padStart(2, "0")}</span>
        <Input value={track.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("Título del track")} className="font-medium" data-testid={`${id}-title`} />
        <Input value={track.version || ""} onChange={(e) => set({ version: e.target.value })} placeholder={t("Versión (Remix, Live…)")} className="max-w-[180px]" data-testid={`${id}-version`} />
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} data-testid={`${id}-remove`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="ISRC" hint="Vacío = se asigna automáticamente">
          <Input value={track.isrc || ""} onChange={(e) => set({ isrc: e.target.value.toUpperCase() })} placeholder="USS1Z2600001" className="font-mono" data-testid={`${id}-isrc`} />
        </Field>
        <Field label="Idioma del audio">
          <SimpleSelect value={track.audio_language} onChange={(v) => set({ audio_language: v })} options={LANGUAGES} testId={`${id}-language`} />
        </Field>
        <Field label="Contenido explícito">
          <div className="flex items-center gap-3 h-10">
            <Switch checked={track.explicit} onCheckedChange={(v) => set({ explicit: v })} data-testid={`${id}-explicit`} />
            <span className="text-sm text-muted-foreground">{track.explicit ? "Parental Advisory" : t("Limpio")}</span>
          </div>
        </Field>
        <Field label="Año ℗">
          <Input type="number" value={track.copyright_year || ""} onChange={(e) => set({ copyright_year: e.target.value ? Number(e.target.value) : null })} placeholder="2026" data-testid={`${id}-copyright-year`} />
        </Field>
        <Field label="Titular ℗">
          <Input value={track.copyright_holder || ""} onChange={(e) => set({ copyright_holder: e.target.value })} placeholder="Doble R Records" data-testid={`${id}-copyright-holder`} />
        </Field>
        <Field label="Editorial / Publisher">
          <Input value={track.publisher || ""} onChange={(e) => set({ publisher: e.target.value })} data-testid={`${id}-publisher`} />
        </Field>
      </div>
      <FileUpload kind="audio" accept=".wav,.flac" label={t("Audio master (WAV o FLAC · no se acepta MP3)")} value={track.audio_file_id} filename={track.audio_filename}
        onUploaded={(f) => set({ audio_file_id: f.id, audio_filename: f.original_filename })} onClear={() => set({ audio_file_id: null, audio_filename: null })} testId={`${id}-audio`} compact />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PeopleList items={track.artists} roles={ARTIST_ROLES} onChange={(artists) => set({ artists })} label="Artistas (hasta 5 · todos se registran como Vocals)" testId={`${id}-artists`} defaultRole="Primary Artist" kind="artist" />
        <PeopleList items={track.contributors} roles={CONTRIBUTOR_ROLES} onChange={(contributors) => set({ contributors })} label="Contributors · compositor/letrista con NOMBRE Y APELLIDO real (= letrista automático) + 1 productor/ingeniero" testId={`${id}-contributors`} defaultRole="Composer" kind="contributor" />
      </div>
      <Field label="Letra">
        <Textarea value={track.lyrics || ""} onChange={(e) => set({ lyrics: e.target.value })} rows={3} placeholder={t("Opcional")} data-testid={`${id}-lyrics`} />
      </Field>
    </div>
  );
};
